import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Alert, Row, Col, Card, Badge, Spinner, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PermissionWrapper from "../components/PermissionWrapper";
import { fetchGroups, createGroup, updateGroup, deleteGroup } from "../services/GroupService";
import GroupData from "../models/GroupData";
import { IMAGE_BASE } from "../Config";
import { getGroupImageUrl, handleImageError } from "../utils/imageUtils";
import { apiGet, apiPost } from "../services/BaseService";

const ManageAllGroupsPage: React.FC = () => {
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalGroups, setTotalGroups] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupData | null>(null);
  const [formData, setFormData] = useState<Partial<GroupData>>({
    name: "",
    description: "",
    email: "",
    phone: "",
    address: "",
    website: "",
    foundedDate: "",
    pinCode: "",
    state: "",
    country: "",
    groupImage: "",
    ownerUser: "",
    isFeatured: false
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [ownerUsers, setOwnerUsers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchGroupsData(true);
    fetchCountries();
    fetchOwnerUsers();
  }, []);

  const fetchCountries = async () => {
    try {
      const data = await apiGet('/countries');
      setCountries(data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStates = async (countryCode: string) => {
    try {
      const data = await apiGet(`/states/country/${countryCode}`);
      setStates(data);
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const fetchOwnerUsers = async () => {
    try {
      const data = await apiGet('/users/by-role');
      setOwnerUsers(data);
    } catch (error) {
      console.error('Failed to fetch owner users:', error);
    }
  };

  const fetchGroupsData = async (reset: boolean = false, searchTerm: string = '', country: string = 'IN', state: string = '') => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }
      
      const currentPage = reset ? 1 : page;
      const response = await fetchGroups(currentPage, 12, searchTerm, country, state);
      
      if (reset) {
        setGroups(response.groups);
      } else {
        setGroups(prev => [...prev, ...response.groups]);
      }
      
      setHasMore(response.hasMore);
      setTotalGroups(response.totalGroups);
      setPage(currentPage + 1);
    } catch (error) {
      setError("Failed to fetch groups");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    fetchGroupsData(true, query, countryFilter, stateFilter);
  };

  const handleCountryFilter = (country: string) => {
    setCountryFilter(country);
    setStateFilter('');
    fetchStates(country);
    fetchGroupsData(true, searchQuery, country, '');
  };

  const handleStateFilter = (state: string) => {
    setStateFilter(state);
    fetchGroupsData(true, searchQuery, countryFilter, state);
  };

  const handleSort = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    
    const sortedGroups = [...groups].sort((a, b) => {
      if (newOrder === 'asc') {
        return a.name.localeCompare(b.name);
      } else {
        return b.name.localeCompare(a.name);
      }
    });
    
    setGroups(sortedGroups);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchGroupsData(false, searchQuery, countryFilter, stateFilter);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery);
    }
  };

  const handleShowModal = (group?: GroupData) => {
    if (group) {
      setEditingGroup(group);
      setFormData({ ...group });
      if (group.country) {
        fetchStates(group.country);
      }
    } else {
      setEditingGroup(null);
      setFormData({
        name: "",
        description: "",
        email: "",
        phone: "",
        address: "",
        website: "",
        foundedDate: "",
        pinCode: "",
        state: "",
        country: "",
        groupImage: "",
        ownerUser: "",
        isFeatured: false,
        isActive: true
      });
      setSelectedFile(null);
      setStates([]);
    }
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'country' && value) {
      fetchStates(value);
      setFormData(prev => ({ ...prev, state: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!selectedFile) return null;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const result = await apiPost('/image/upload/groupLogoImage', formData);
      return `/groupLogoImage/${result.fileName}`;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let imageFileName: any = formData.groupImage;
      
      if (selectedFile) {
        imageFileName = await uploadImage();
      }
      
      const submitData = { ...formData, groupImage: imageFileName || '' };
      
      if (editingGroup) {
        await updateGroup(editingGroup._id!, submitData as GroupData);
        setSuccess("Group updated successfully");
      } else {
        await createGroup(submitData as GroupData);
        setSuccess("Group created successfully");
      }
      await fetchGroupsData(true, searchQuery, countryFilter, stateFilter);
      handleCloseModal();
    } catch (error) {
      setError(editingGroup ? "Failed to update group" : "Failed to create group");
    }
  };

  const handleDelete = async (groupId: string) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      try {
        await deleteGroup(groupId);
        setSuccess("Group deleted successfully");
        await fetchGroupsData(true, searchQuery, countryFilter, stateFilter);
      } catch (error) {
        setError("Failed to delete group");
      }
    }
  };

  return (
    <PermissionWrapper pageName="ManageAllGroupsPage">
      <div>
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h3 className="mb-3 mb-md-0">
                <i className="bi bi-collection me-2"></i>
                Manage All Groups
              </h3>
              <Button variant="primary" onClick={() => handleShowModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Group
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search groups by name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button variant="outline-secondary" onClick={() => handleSearch(searchQuery)}>
                <i className="bi bi-search"></i>
              </Button>
            </InputGroup>
          </Col>
          <Col md={2}>
            <Form.Select
              value={countryFilter}
              onChange={(e) => handleCountryFilter(e.target.value)}
            >
              <option value="">Select Country</option>
              {countries.map(country => (
                <option key={country.countryCode} value={country.countryCode}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Select
              value={stateFilter}
              onChange={(e) => handleStateFilter(e.target.value)}
              disabled={!countryFilter}
            >
              <option value="">All States</option>
              {states.map(state => (
                <option key={state.stateCode} value={state.stateCode}>
                  {state.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Button 
              variant="outline-secondary" 
              onClick={handleSort}
              className="w-100"
            >
              <i className={`bi bi-sort-alpha-${sortOrder === 'asc' ? 'down' : 'up'} me-1`}></i>
              A-Z
            </Button>
          </Col>
          <Col md={3} className="d-flex align-items-center">
            <small className="text-muted">
              {groups.length > 0 ? `Showing ${groups.length} of ${totalGroups} groups` : "No groups found"}
              {searchQuery && ` for "${searchQuery}"`}
            </small>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <Row className="g-3">
            {groups.map((group) => (
              <Col xs={12} sm={6} md={4} lg={3} key={group._id}>
                <Card className="h-100 group-card">
                  <div className="group-image-container">
                    <Card.Img 
                      variant="top" 
                      src={getGroupImageUrl(group.groupImage)}
                      className="group-image"
                      onError={(e) => handleImageError(e, 'GROUP')}
                    />
                  </div>
                  <Card.Body className="d-flex flex-column">
                    <Card.Title className="h6">{group.name}</Card.Title>
                    <Card.Text className="small text-muted flex-grow-1">
                      {group.description ? 
                        (group.description.length > 80 ? 
                          group.description.substring(0, 80) + '...' : 
                          group.description
                        ) : 'No description available'
                      }
                    </Card.Text>
                    <div className="group-info mb-2">
                      {group.email && (
                        <div className="small">
                          <i className="bi bi-envelope me-1"></i>
                          {group.email}
                        </div>
                      )}
                      {group.phone && (
                        <div className="small">
                          <i className="bi bi-telephone me-1"></i>
                          {group.phone}
                        </div>
                      )}
                      {group.website && (
                        <div className="small">
                          <i className="bi bi-globe me-1"></i>
                          <a href={group.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                            Website
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-1 mt-auto">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleShowModal(group)}
                        className="flex-fill"
                      >
                        <i className="bi bi-pencil me-1"></i>
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(group._id!)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        
        {/* Load More Button */}
        {hasMore && (
          <Row className="mt-4">
            <Col xs={12} className="text-center">
              <Button 
                variant="outline-primary" 
                size="lg" 
                onClick={handleLoadMore} 
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  "Load More Groups"
                )}
              </Button>
            </Col>
          </Row>
        )}

        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingGroup ? "Edit Group" : "Add Group"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Group Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Website</Form.Label>
                    <Form.Control
                      type="url"
                      name="website"
                      value={formData.website || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Founded Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="foundedDate"
                      value={formData.foundedDate || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pin Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="pinCode"
                      value={formData.pinCode || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Select
                      name="country"
                      value={formData.country || ""}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map(country => (
                        <option key={country.countryCode} value={country.countryCode}>
                          {country.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>State</Form.Label>
                    <Form.Select
                      name="state"
                      value={formData.state || ""}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.country}
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state.stateCode} value={state.stateCode}>
                          {state.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Owner User</Form.Label>
                <Form.Select
                  name="ownerUser"
                  value={formData.ownerUser || ""}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Owner</option>
                  {ownerUsers.map(user => (
                    <option key={user._id} value={user._id}>
                      {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username} ({user.email})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address || ""}
                  onChange={handleInputChange}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Group Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Upload a logo or image for the group (JPG, PNG, GIF, SVG)
                </Form.Text>
                {formData.groupImage && (
                  <div className="mt-2">
                    <img 
                      src={getGroupImageUrl(formData.groupImage)}
                      alt="Current group image"
                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                      className="rounded"
                      onError={(e) => handleImageError(e, 'GROUP')}
                    />
                  </div>
                )}
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description || ""}
                  onChange={handleInputChange}
                />
              </Form.Group>

              {editingGroup && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="group-featured-switch"
                      label="Featured Group"
                      checked={formData.isFeatured || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                    />
                    <Form.Text className="text-muted">
                      Featured groups appear prominently on the homepage
                    </Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="group-active-switch"
                      label="Active Group"
                      checked={formData.isActive !== false}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                    <Form.Text className="text-muted">
                      Only active groups are visible to users
                    </Form.Text>
                  </Form.Group>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  <>{editingGroup ? "Update" : "Create"} Group</>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </PermissionWrapper>
  );
};

export default ManageAllGroupsPage;